import React, { useMemo, useState, useRef, useEffect } from "react";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";
import type { CountryCode } from "libphonenumber-js";
import {
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from "libphonenumber-js";
import flags from "react-phone-number-input/flags";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { ChevronDown } from "lucide-react";

const DEFAULT_COUNTRY: CountryCode = "CH";

const displayNames = new Intl.DisplayNames(["en"], { type: "region" });

function getCountryName(country: CountryCode): string {
  try {
    return displayNames.of(country) ?? country;
  } catch {
    return country;
  }
}

const COUNTRY_OPTIONS = getCountries().map((country) => ({
  country: country as CountryCode,
  code: getCountryCallingCode(country),
  name: getCountryName(country as CountryCode),
}));

/** Strip a country's calling code from the start of an E.164 string to get national digits. */
function stripCallingCode(value: string, country: CountryCode): string {
  const code = getCountryCallingCode(country);
  const prefix = `+${code}`;
  if (value.startsWith(prefix)) {
    return value.slice(prefix.length).replace(/\D/g, "");
  }
  return value.replace(/\D/g, "");
}

function parseValue(
  value: string | undefined,
  lastCountryRef: React.MutableRefObject<CountryCode>
): { country: CountryCode; nationalNumber: string } {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) {
    return { country: lastCountryRef.current, nationalNumber: "" };
  }
  const parsed = parsePhoneNumberFromString(trimmed);
  if (parsed) {
    const parsedCountry = parsed.country as CountryCode | undefined;
    if (parsedCountry) {
      lastCountryRef.current = parsedCountry;
      return {
        country: parsedCountry,
        nationalNumber: parsed.nationalNumber,
      };
    }
    // Incomplete number (e.g. +171): libphonenumber knows digits but not the region yet.
    const country = lastCountryRef.current;
    return {
      country,
      nationalNumber: parsed.nationalNumber || stripCallingCode(trimmed, country),
    };
  }
  // Value is only "+XX" (country code): resolve country so UI shows correct flag
  if (/^\+\d+$/.test(trimmed)) {
    const code = trimmed.slice(1);
    const lastCode = getCountryCallingCode(lastCountryRef.current);
    if (lastCode === code) {
      return { country: lastCountryRef.current, nationalNumber: "" };
    }
    const byCode = COUNTRY_OPTIONS.find((o) => o.code === code);
    if (byCode) {
      lastCountryRef.current = byCode.country;
      return { country: byCode.country, nationalNumber: "" };
    }
  }
  const country = lastCountryRef.current;
  const nationalNumber = stripCallingCode(trimmed, country);
  return { country, nationalNumber };
}

/** Build E.164 value. If no digits, returns "+{code}" so the selected country is persisted and UI updates. */
function buildE164(country: CountryCode, nationalDigits: string): string {
  const digits = nationalDigits.replace(/\D/g, "");
  const code = getCountryCallingCode(country);
  if (!digits) return `+${code}`;
  return `+${code}${digits}`;
}

/** Global cap for national digits (E.164); per-country max is enforced via validatePhoneNumberLength. */
const GLOBAL_MAX_NATIONAL_DIGITS = 15;

export interface PhoneCountryInputProps {
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  hasError?: boolean;
  className?: string;
  id?: string;
}

export function PhoneCountryInput({
  value,
  onChange,
  onBlur,
  placeholder = "Mobile number",
  hasError,
  className,
  id,
}: PhoneCountryInputProps) {
  const [open, setOpen] = useState(false);
  const lastCountryRef = useRef<CountryCode>(DEFAULT_COUNTRY);

  const { country, nationalNumber } = useMemo(
    () => parseValue(value, lastCountryRef),
    [value]
  );

  useEffect(() => {
    lastCountryRef.current = country;
  }, [country]);

  const currentOption = useMemo(
    () => COUNTRY_OPTIONS.find((o) => o.country === country) ?? COUNTRY_OPTIONS.find((o) => o.country === DEFAULT_COUNTRY)!,
    [country]
  );

  const FlagComponent = flags[country] ?? flags[DEFAULT_COUNTRY];

  const handleSelectCountry = (newCountry: CountryCode) => {
    setOpen(false);
    lastCountryRef.current = newCountry;
    const newValue = buildE164(newCountry, nationalNumber);
    onChange(newValue);
  };

  const handleNationalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const activeCountry = country ?? lastCountryRef.current;
    let digits = e.target.value.replace(/\D/g, "").slice(0, GLOBAL_MAX_NATIONAL_DIGITS);
    // Enforce per-country max length: don't allow digits that make the number TOO_LONG.
    while (
      digits.length > 0 &&
      validatePhoneNumberLength(buildE164(activeCountry, digits)) === "TOO_LONG"
    ) {
      digits = digits.slice(0, -1);
    }
    onChange(buildE164(activeCountry, digits));
  };

  return (
    <div
      className={cn(
        "flex w-full rounded-xl border bg-white overflow-hidden",
        hasError ? "border-red-500 bg-red-50/50" : "border-gray-200",
        className
      )}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-1.5 shrink-0 px-3 py-2 text-neutral-900 border-r border-gray-200 hover:bg-pine-green/[0.06] transition-colors outline-none focus:ring-2 focus:ring-alpine-gold/30 focus:ring-inset"
            aria-label="Country code"
          >
            {FlagComponent && (
              <span className="w-5 h-[0.75rem] overflow-hidden rounded-sm flex items-center justify-center">
                {React.createElement(FlagComponent as React.ComponentType<{ title?: string }>, {
                  title: getCountryName(country),
                })}
              </span>
            )}
            <span className="text-sm font-medium">+{currentOption.code}</span>
            <ChevronDown className={cn("h-4 w-4 opacity-60 transition-transform", open && "rotate-180")} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search country or code..." />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {COUNTRY_OPTIONS.map((opt) => {
                  const FlagItem = flags[opt.country];
                  return (
                    <CommandItem
                      key={opt.country}
                      value={`${opt.name} +${opt.code} ${opt.country}`}
                      onSelect={() => handleSelectCountry(opt.country)}
                      className="flex items-center gap-2 py-2 rounded-sm hover:bg-pine-green/[0.06] data-[selected=true]:bg-pine-green/[0.08]"
                    >
                      {FlagItem && (
                        <span className="w-5 h-[0.75rem] overflow-hidden rounded-sm shrink-0 flex items-center justify-center">
                          {React.createElement(FlagItem as React.ComponentType<{ title?: string }>, {
                            title: opt.name,
                          })}
                        </span>
                      )}
                      <span className="text-sm text-neutral-900 font-medium">+{opt.code}</span>
                      <span className="text-sm text-neutral-900">{opt.name}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <input
        id={id}
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        placeholder={placeholder}
        value={nationalNumber}
        onChange={handleNationalChange}
        onBlur={onBlur}
        maxLength={GLOBAL_MAX_NATIONAL_DIGITS}
        className="flex-1 min-w-0 px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-900/50 bg-transparent border-0 outline-none focus:ring-0"
      />
    </div>
  );
}
